import type { shell as ru } from '../ru/shell';

export const shell: typeof ru = {
  nav: {
    matches: 'Matches',
    statistics: 'Statistics',
    players: 'Players',
    store: 'Store',
    banPresets: 'Ban presets',
    admin: 'Admin',
    analytics: 'Analytics',
    permissions: 'Roles & permissions',
    tags: 'Player tags',
    catalogs: 'Catalogs',
    matchSettings: 'Match settings',
    promoCodes: 'Promo codes',
    polls: 'Polls',
    dedicatedKeys: 'Server keys',
    accounting: 'Accounting'
  },
  pageTitle: {
    player: 'Player',
    match: 'Match'
  },
  app: {
    steamValidationFailed: 'Could not verify your Steam sign-in. Please try again.',
    accessDenied: 'Access to this section is denied.',
    accountDisabled: 'The account is disabled.',
    authError: (code: string) => `Authorization error: ${code}`,
    sessionFetchFailed: 'Could not fetch the current session.',
    sessionKicker: 'Session',
    checkingAccess: 'Checking access...',
    noPlayerProfileTitle: 'No game profile',
    noPlayerProfileDescription: 'You have no linked game profile. Join the custom game to register.',
    presetsForbiddenTitle: 'No access to ban presets',
    presetsForbiddenDescription: 'Your account is not allowed to edit ban presets.'
  },
  auth: {
    loginRequiredTitle: 'Sign in to continue',
    adminLoginDescription: 'This section is available to administrators only. Sign in with Steam first.',
    adminOnlyTitle: 'Administrators only',
    adminOnlyDescription: 'Your account is not allowed to view this section.'
  },
  catalog: {
    loadFailed: 'Failed to load the catalog'
  },
  avatar: {
    online: 'Online'
  },
  cookieBanner: {
    textPrefix: 'This site uses only the cookies necessary for sign-in and session operation.',
    link: 'Learn more in the Policy',
    dismiss: 'Got it'
  },
  dateInput: {
    placeholder: 'dd.mm.yyyy'
  },
  systemStates: {
    errorTitle: 'Something went wrong',
    forbiddenTitle: 'Access denied',
    forbiddenDescription: 'You do not have permission to view this section.',
    loadingTitle: 'Loading...',
    loginGateTitle: 'Sign in to continue',
    loginGateDescription: 'Steam authorization is required to view this section.',
    whatIsAvailable: 'What is available without signing in?',
    readOnly: 'View only — changes are unavailable'
  },
  tableSkeleton: {
    ariaLabel: 'Loading data'
  },
  legal: {
    documents: 'Legal information',
    documentsTitle: 'Documents',
    offer: 'Public offer'
  },
  privacy: {
    kicker: 'Legal information',
    title: 'Personal Data Processing Policy',
    revision: (date: string) => `Revision dated ${date}`,
    operator: {
      title: '1. Personal data operator and contacts',
      intro: (siteUrl: string) =>
        `The operator of personal data of users of the website ${siteUrl} (hereinafter the "Website", the "Service") is the administration of the ratten.run website.`,
      contactPrefix:
        'For questions about personal data processing, as well as to withdraw consent to processing or to request data deletion, contact us by email: ',
      contactSuffix: '.'
    },
    data: {
      title: '2. What data is processed',
      items: [
        'The user\'s Steam ID, public nickname and Steam avatar (obtained via Steam OpenID at sign-in).',
        'Game statistics: match results, rating (SOLO/DUO), player tags, ban history.',
        'Session service data: refresh token (stored in a cookie), session identifier.',
        'Store order data: email, game UID (only for the VIP subscription), order number, contents, amount and status. The Website does not receive or store bank card data.',
        'Technical request logs: IP address, User-Agent, request identifier.'
      ]
    },
    purposes: {
      title: '3. Purposes of processing',
      items: [
        'Identification and authentication of the user via Steam.',
        'Providing the Service features: displaying statistics, ratings and match history.',
        'Maintaining the game session and protecting against unauthorized access.',
        'Moderation: handling of tags, bans and reports.',
        'Placing and fulfilling store orders: issuing a code or key, activating the subscription, sending order emails, issuing receipts, answering requests and processing refunds.',
        'Diagnosing technical issues and ensuring the security of the Service.'
      ]
    },
    legalBasis: {
      title: '4. Legal grounds for processing',
      text: 'Processing is carried out on the basis of the user\'s consent (expressed by an action — signing in via Steam), as well as for the purpose of performing the terms of use of the Service and the digital services contract (the store\'s public offer) and on the basis of the operator\'s legitimate interests (ensuring security, preventing fraud and violations of the game rules).'
    },
    storage: {
      title: '5. Methods and retention periods',
      text: 'Data is stored in electronic form on a server located in the territory of the Russian Federation (hosting provider FirstVDS). Game statistics and the profile are stored for as long as the Service is used. The session refresh token is stored in a cookie for up to 90 days or until you sign out. Order data is stored no longer than needed to fulfil the order, process refunds and meet legal requirements, then deleted or anonymized. Technical logs are stored for a limited period necessary for diagnostics and security.'
    },
    thirdParties: {
      title: '6. Disclosure of data to third parties',
      valveName: 'Valve Corporation (Steam)',
      valveText:
        '— used as an external sign-in provider via the OpenID protocol. The Service receives from Steam only the Steam ID, public nickname and avatar; your Steam password is neither transmitted to nor stored by the Service.',
      hostingName: 'Hosting provider',
      hostingText:
        '— the server hosting the Website and the database is located in the territory of the Russian Federation (FirstVDS).',
      paymentName: 'Payment services',
      paymentText:
        '— receive the data needed to process payment (amount, order number, email for the receipt). Bank card data is entered on the payment service side and is not passed to the operator.',
      mailName: 'Email service',
      mailText: '— used to send order emails (email address and message content).',
      gameName: 'Ratten Run game server and bot manager',
      gameText: '— receive the game UID to activate the subscription and deliver the purchase.',
      noOthers: 'Personal data is not disclosed to any other third parties and is not used for advertising or marketing purposes.'
    },
    rights: {
      title: '7. Rights of the data subject',
      lead: 'The user has the right to:',
      items: [
        'obtain information about the composition and purposes of processing of their data;',
        'withdraw consent to the processing of personal data;',
        'demand deletion, rectification or blocking of their data.'
      ],
      requestPrefix: 'To exercise these rights, send a request to the operator\'s email',
      requestSuffix:
        '. Please note: withdrawing consent or deleting data may make further use of the Service impossible (for example, signing in via Steam or displaying game statistics).'
    },
    cookies: {
      title: '8. Cookies',
      text: 'The Website uses only strictly necessary cookies that provide sign-in and session operation. Analytics and advertising cookies are not used.',
      columnName: 'Name',
      columnPurpose: 'Purpose',
      columnRetention: 'Retention period',
      sessionPurpose: 'Server-side HTTP session identifier',
      sessionRetention: 'Until the session / browser is closed',
      refreshPurpose:
        'Extends the sign-in session without re-authorizing via Steam (httpOnly, not accessible from JS)',
      refreshRetention: 'Up to 90 days or until you sign out',
      localStorage:
        'The Website also stores in the browser\'s local storage (localStorage) the fact that the cookie notice was dismissed, as well as order numbers and their access tokens used to check order status — this data does not leave your device and is not sent to the server. In addition, it stores rr_visitor_id — a random identifier used to count visitors online: it is not linked to your account, is sent to the server and kept there for no longer than a few minutes.'
    },
    changes: {
      title: '9. Changes to the policy',
      prefix: 'The operator may amend this Policy. The current revision is always available at',
      suffix: '. The revision date is shown at the beginning of the document.'
    }
  }
};
